import { useEffect, useRef } from "react";

// Fires `onIntersect` when a sentinel element scrolls into view, for
// "load next page" lists. `rootMargin` defaults to firing ~300px before the
// sentinel is actually visible so the next page prefetches ahead of the user
// hitting bottom, rather than after — root: null (the browser viewport) still
// tracks correctly even though this app's real scroll container is an inner
// div (side-navigation.component's .component-wrapper), since scrolling that
// div moves the sentinel's on-screen position the same way window-scroll would.
export function useInfiniteScrollSentinel(
	onIntersect: () => void,
	{ enabled = true, rootMargin = "300px" }: { enabled?: boolean; rootMargin?: string } = {},
) {
	const sentinelRef = useRef<HTMLDivElement | null>(null);
	const onIntersectRef = useRef(onIntersect);
	onIntersectRef.current = onIntersect;

	useEffect(() => {
		const node = sentinelRef.current;
		if (!node || !enabled) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					onIntersectRef.current();
				}
			},
			{ rootMargin },
		);

		observer.observe(node);
		return () => observer.disconnect();
	}, [enabled, rootMargin]);

	return sentinelRef;
}
